import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Communication, Reference } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GroupMemberInfo } from '../data';
import {
  dedupeCommunicationsById,
  fetchThreadMessages,
  fetchRepliesForMessage,
  fetchReplyCountsForMessages,
  fetchParticipantDisplayName,
  isDirectMessagingParticipantRef,
  participantDisplayLabel,
} from '../services/messagingFhir';
import { resolveMessagingCurrentUserRef } from '../services/messagingIdentity';
import { useCommunicationSubscription } from './useCommunicationSubscription';
import { notifyUnreadStateChanged } from './useUnreadThreadCount';

/**
 * Provides all data needed by ThreadView for a given thread/sub-thread.
 * Subscribes to realtime updates via `Communication?part-of=Communication/{threadId}`
 * (or parentMessageId for sub-threads), matching the Medplum demo pattern.
 * This avoids `meta.account` compartment mismatches that block subscription delivery
 * when `recipient`-based criteria are used across different access policy compartments.
 */
export function useThreadData(
  threadId: string | null,
  parentMessageId: string | null,
  options?: {
    extraThreads?: Communication[];
    initialMessages?: Communication[];
  },
): {
  thread: Communication | undefined;
  messages: Communication[];
  setMessages: React.Dispatch<React.SetStateAction<Communication[]>>;
  threadReplies: Communication[];
  setThreadReplies: React.Dispatch<React.SetStateAction<Communication[]>>;
  loading: boolean;
  parentMessage: Communication | null;
  groupMembers: GroupMemberInfo[];
  getReplyCount: (messageId: string) => number;
  currentUserRef: Reference;
  isMock: boolean;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [thread, setThread] = useState<Communication | undefined>();
  const [messages, setMessages] = useState<Communication[]>([]);
  const [threadReplies, setThreadReplies] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(false);
  const [parentMsg, setParentMsg] = useState<Communication | null>(null);
  const [replyCounts, setReplyCounts] = useState<Record<string, number>>({});
  const [groupMembers, setGroupMembers] = useState<GroupMemberInfo[]>([]);

  const currentUserRef = useMemo((): Reference => {
    return resolveMessagingCurrentUserRef(profile, false);
  }, [profile]);

  // ── Resolve thread root ──────────────────────────────────────────────────
  useEffect(() => {
    if (!threadId) {
      setThread(undefined);
      return;
    }
    const extra = options?.extraThreads?.find((t) => t.id === threadId);
    if (extra) {
      setThread(extra);
    } else {
      medplum
        .readResource('Communication', threadId)
        .then((t) => setThread(t))
        .catch(() => setThread(undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, medplum]);

  // ── Load messages or sub-thread replies ──────────────────────────────────
  const loadMessages = useCallback(async () => {
    if (!threadId) {
      setMessages([]);
      setThreadReplies([]);
      setReplyCounts({});
      return;
    }

    setLoading(true);

    try {
      if (parentMessageId) {
        const replies = await fetchRepliesForMessage(medplum, parentMessageId);
        setThreadReplies(replies);
        setMessages([]);
      } else {
        const msgs = dedupeCommunicationsById(await fetchThreadMessages(medplum, threadId));
        setMessages(msgs.length > 0 ? msgs : (options?.initialMessages ?? []));
        setThreadReplies([]);

        const msgIds = msgs.map((m) => m.id).filter(Boolean) as string[];
        if (msgIds.length > 0) {
          const counts = await fetchReplyCountsForMessages(medplum, msgIds);
          setReplyCounts(counts);
        }
      }
    } catch (err) {
      console.error('[Messaging] Failed to load messages:', err);
      setMessages([]);
      setThreadReplies([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, parentMessageId, medplum]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  // ── Realtime subscription ────────────────────────────────────────────────
  // Subscribe to `Communication?part-of=Communication/{target}` so both
  // participants see every message posted to the thread in realtime.
  // This bypasses the `meta.account` compartment mismatch that blocks
  // `recipient`-based subscriptions across different access policy compartments.
  const activeTarget = parentMessageId ?? threadId;

  const subscriptionCriteria = useMemo(() => {
    if (!activeTarget) return undefined;
    return `Communication?part-of=Communication/${activeTarget}`;
  }, [activeTarget]);

  useCommunicationSubscription(subscriptionCriteria, {
    onCommunicationEvent: useCallback(
      (comm: Communication) => {
        if (parentMessageId) {
          setThreadReplies((prev) => {
            if (prev.some((m) => m.id === comm.id)) return prev;
            return [...prev, comm];
          });
        } else {
          setMessages((prev) => {
            const existingIndex = prev.findIndex((m) => m.id === comm.id);
            if (existingIndex >= 0) {
              const next = [...prev];
              next[existingIndex] = comm;
              return next;
            }
            if (prev.some((m) => m.id === comm.id)) return prev;
            return [...prev, comm];
          });
        }
        // Notify sidebar / nav badge so they refresh when a realtime message
        // arrives via the thread-scoped subscription (which bypasses meta.account).
        notifyUnreadStateChanged();
      },
      [parentMessageId],
    ),
    onReconnect: useCallback(() => {
      void loadMessages();
    }, [loadMessages]),
  });

  // ── Resolve parent message (sub-thread view header) ──────────────────────
  useEffect(() => {
    if (!parentMessageId || !threadId) {
      setParentMsg(null);
      return;
    }
    medplum
      .readResource('Communication', parentMessageId)
      .then((m) => setParentMsg(m))
      .catch(() => setParentMsg(null));
  }, [parentMessageId, threadId, medplum]);

  // ── Group / direct participants (exclude Group/{id} container refs) ───────
  useEffect(() => {
    if (!threadId || !thread) {
      setGroupMembers([]);
      return;
    }

    let cancelled = false;

    const toDirectParticipantMember = (ref: Reference | undefined): GroupMemberInfo | null => {
      const reference = ref?.reference;
      if (!reference || !isDirectMessagingParticipantRef(reference)) {
        return null;
      }
      return {
        reference,
        display: participantDisplayLabel(ref) ?? '',
      };
    };

    const directFromRecipient = (thread.recipient ?? [])
      .map(toDirectParticipantMember)
      .filter((m): m is GroupMemberInfo => m !== null);

    const dedupeMembers = (members: GroupMemberInfo[]): GroupMemberInfo[] => {
      const seen = new Set<string>();
      return members.filter((m) => {
        if (seen.has(m.reference)) return false;
        seen.add(m.reference);
        return true;
      });
    };

    const enrichMemberDisplays = async (members: GroupMemberInfo[]): Promise<GroupMemberInfo[]> =>
      Promise.all(
        members.map(async (member) => {
          if (member.display) return member;
          const resolved = await fetchParticipantDisplayName(medplum, member.reference);
          return { ...member, display: resolved ?? 'Unknown' };
        }),
      );

    const groupSubjectRef = thread.subject?.reference;
    if (!groupSubjectRef?.startsWith('Group/')) {
      void enrichMemberDisplays(dedupeMembers(directFromRecipient)).then((members) => {
        if (!cancelled) setGroupMembers(members);
      });
      return;
    }

    void (async () => {
      try {
        const groupId = groupSubjectRef.replace('Group/', '');
        const group = await medplum.readResource('Group', groupId);
        const fromGroup = (group.member ?? [])
          .map((m) => toDirectParticipantMember(m.entity))
          .filter((m): m is GroupMemberInfo => m !== null);

        if (!cancelled) {
          setGroupMembers(
            await enrichMemberDisplays(dedupeMembers([...fromGroup, ...directFromRecipient])),
          );
        }
      } catch {
        if (!cancelled) {
          setGroupMembers(await enrichMemberDisplays(dedupeMembers(directFromRecipient)));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [threadId, thread, medplum]);

  // ── Reply counts ─────────────────────────────────────────────────────────
  const getReplyCount = useCallback(
    (messageId: string): number => {
      return replyCounts[messageId] ?? 0;
    },
    [replyCounts],
  );

  return {
    thread,
    messages,
    setMessages,
    threadReplies,
    setThreadReplies,
    loading,
    parentMessage: parentMsg,
    groupMembers,
    getReplyCount,
    currentUserRef,
    isMock: false,
  };
}
